import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { authApi } from "../api/services";

// ✅ Dashboard와 일관된 색상 팔레트 정의
const colorPalette = {
  primary: "#2d6cdf",
  secondary: "#f1f2f6",
  border: "#dcdfe6",
  textPrimary: "#1a1f36",
  textSecondary: "#4f566b",
  background: "#f5f7fa",
  white: "#ffffff",
};

// MUI 테마 설정
const theme = createTheme({
  palette: {
    primary: {
      main: colorPalette.primary,
    },
    background: {
      default: colorPalette.background,
    },
  },
  typography: {
    fontFamily: "'Inter', 'Noto Sans KR', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          padding: "12px 16px",
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${colorPalette.border}`,
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
  },
});


/**
 * 로그인 페이지 컴포넌트
 * @param {Function} onLoginSuccess - 로그인 성공 시 실행할 콜백
 */
const Login = ({ onLoginSuccess }) => {
  const savedEmail = localStorage.getItem("mallang_saved_email") || "";
  const [email, setEmail] = useState(savedEmail);
  const [rememberEmail, setRememberEmail] = useState(Boolean(savedEmail));
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // 로그인 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await authApi.login(email, password);

      const {
        access_token, 
        refresh_token, 
        user_type, 
        admin // 새롭게 추가된 admin 객체
      } = response.data;


        
        // admin 객체에서 필요한 정보 추출
        const userData = {
            name: admin?.name || "관리자",
            organization: admin?.organization || "소속 없음",
            level: admin?.level,
            code: admin?.code,
            email: email || admin?.email,
            user_type
        };

        // 토큰 및 사용자 정보 저장
        localStorage.setItem("mallang_access_token", access_token);
        localStorage.setItem("mallang_refresh_token", refresh_token);
        localStorage.setItem("mallang_user", JSON.stringify(userData));
        if (rememberEmail) {
          localStorage.setItem("mallang_saved_email", email);
        } else {
          localStorage.removeItem("mallang_saved_email");
        }

        // 상위 컴포넌트에 알림
        if (onLoginSuccess) {
            onLoginSuccess(access_token, userData);
        }
      
    } catch (err) {
      // console.error("Login failed:", err);
      if (err.response?.status === 401) {
        setError("이메일 또는 비밀번호가 올바르지 않습니다.");
      } else {
        setError("서버와의 통신 중 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${colorPalette.primary}0a 0%, ${colorPalette.primary}1a 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* 디자인 장식 요소 */}
        <Box
          sx={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colorPalette.primary}10 0%, transparent 70%)`,
            top: -100,
            right: -100,
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colorPalette.primary}08 0%, transparent 70%)`,
            bottom: -50,
            left: -50,
            zIndex: 0,
          }}
        />

        <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(20px)",
            }}
          >
            {/* 로고 영역 */}
            <Box sx={{ mb: 5, textAlign: "center" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  color: colorPalette.primary,
                  mb: 1,
                  letterSpacing: "-1px",
                }}
              >
                말랑포켓
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: colorPalette.textSecondary,
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase"
                }}
              >
                관리자 페이지
              </Typography>
            </Box>

            {/* 로그인 폼 */}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>
                Email Address
              </Typography>
              <TextField
                required
                fullWidth
                id="email"
                placeholder="email@example.com"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>
                Password
              </Typography>
              <TextField
                required
                fullWidth
                name="password"
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                    size="small"
                  />
                }
                label="이메일 저장"
                sx={{
                  mb: 3,
                  ml: -0.5,
                  color: colorPalette.textSecondary,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.8,
                  fontSize: "1rem",
                  boxShadow: `0 8px 16px 0 rgba(45, 108, 223, 0.24)`,
                  '&:hover': {
                    boxShadow: `0 12px 20px 0 rgba(45, 108, 223, 0.32)`,
                  }
                }}
              >
                {loading ? "로그인 중..." : "로그인"}
              </Button>

              <Typography 
                variant="caption" 
                sx={{ 
                  display: "block", 
                  textAlign: "center", 
                  mt: 4, 
                  color: colorPalette.textSecondary 
                }}
              >
                © 2026 Mallang Pocket. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
